import { DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { GridRouteReuseStrategy } from './core/routing/grid-route-reuse-strategy.service';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieService } from 'ngx-cookie-service';
import { ToastrModule } from 'ngx-toastr';

import {
  DATA_GRID_ADAPTER,
  registerSensikaGridModules,
} from '@sensika/ag-grid-kit';
import { environment } from '@env/environment';
import { appInitFactory } from './core/app-init.factory';
import { authInitFactory } from './core/auth-init.factory';
import { HelpPanelPortalService } from '@help/panel/help-panel-portal.service';
import { StoreService } from '@services/store.service';

import { APP_ROUTES } from './app.routes';
import { UtilsApiService } from './api/services/utils-api.service';
import { registerAppIcons } from './core/app-icons';
import { SensikaHttpInterceptor } from './core/http.interceptor';
import { AuthFlowService } from './core/services/auth-flow.service';
import { AuthService } from './core/services/auth.service';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { BREADCRUMB_SERVICE } from './core/services/breadcrumb.token';
import { DataGridAdapterService } from './core/services/data-grid-adapter.service';
import { ObjectEditorService } from './core/services/object-editor.service';
import { OBJECT_EDITOR_SERVICE } from './core/services/object-editor.token';
import { SERVER_FAULT_PRESENTER } from './core/services/server-fault.token';
import { ServerFaultService } from './core/services/server-fault.service';
import { PICKER_DATAOBJECTS_DIALOG } from './core/services/picker-dataobjects-dialog.token';
import { WidgetViewPresetService } from './core/services/widget-view-preset.service';
import { BuildStampService } from './core/services/build-stamp.service';
import { reducers } from './core/state/app.state';
import { AuthEffects } from './core/state/auth.effects';
import { FeedEffects } from './core/state/feed.effects';
import { ShareEffects } from './core/state/share.effects';
import { WidgetEffects } from './core/state/widget.effects';
import { PickerDataobjectsDialogComponent } from './objects/dialogs/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import {
  CustomTranslateHttpLoader,
  MissingTranslationService,
} from './shared/missing.translation.service';

/**
 * Angular Query's default network mode pauses a query or mutation while the browser
 * reports itself offline, so a save would spin forever with no feedback. 'always'
 * sends the request, which fails with status 0 and reaches the fault dialog as a
 * connection problem (WAP-1979, error-presentation.md).
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { networkMode: 'always' },
      mutations: { networkMode: 'always' },
    },
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(
      APP_ROUTES,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        // v22 changed the default to 'always'; pinned to the pre-v22 behaviour so
        // child routes keep seeing only their own params. Revisit deliberately.
        paramsInheritanceStrategy: 'emptyOnly',
      }),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    // Grid routes flagged `reuseAcrossEditor` stay alive across their rule editor round trip (WAP-2025).
    { provide: RouteReuseStrategy, useClass: GridRouteReuseStrategy },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: CustomTranslateHttpLoader,
          deps: [UtilsApiService],
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: MissingTranslationService,
        },
      }),
      AngularSvgIconModule.forRoot(),
      ToastrModule.forRoot({
        positionClass: 'toast-top-center',
        timeOut: 3000,
        closeButton: true,
        progressBar: true,
        tapToDismiss: false,
        preventDuplicates: true,
        includeTitleDuplicates: true,
      }),
    ),
    provideStore(reducers),
    provideEffects([AuthEffects, FeedEffects, WidgetEffects, ShareEffects]),
    !environment.production
      ? provideStoreDevtools({
          name: ':: Sensika ::',
          maxAge: 25,
          // * logOnly: true  -> restricts DevTools to read-only mode (recommended for Production)
          // * logOnly: false -> enables full features: action replay, time-travel, manual dispatching (good for Dev)
          logOnly: !environment.production,
          serialize: {
            options: {
              map: true,
            },
          },
          connectInZone: true,
        })
      : [],
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SensikaHttpInterceptor,
      multi: true,
    },
    CookieService,
    // runtime alias for consumers that must not import the service class (import cycle), see object-editor.token.ts
    { provide: OBJECT_EDITOR_SERVICE, useExisting: ObjectEditorService },
    { provide: SERVER_FAULT_PRESENTER, useExisting: ServerFaultService },
    { provide: BREADCRUMB_SERVICE, useExisting: BreadcrumbService },
    {
      provide: PICKER_DATAOBJECTS_DIALOG,
      useValue: PickerDataobjectsDialogComponent,
    },
    // the grid stack's only door into the app, see ag-grid/data-grid.adapter.ts
    { provide: DATA_GRID_ADAPTER, useExisting: DataGridAdapterService },
    provideAppInitializer(() =>
      appInitFactory(inject(TranslateService), inject(Injector))(),
    ),
    provideAppInitializer(() =>
      authInitFactory(
        inject(AuthService),
        inject(AuthFlowService),
        inject(Store),
      )(),
    ),
    // Root providers are lazy, but this service's changeWidgetsLanaguage
    // derivation must run from startup (see its doc comment) - construct it here.
    provideAppInitializer(() => {
      inject(WidgetViewPresetService);
    }),
    // Startup side effects the bucket modules used to run on import (WAP-1785).
    provideAppInitializer(registerAppIcons),
    // New-build detection poll (WAP-1816).
    provideAppInitializer(() => inject(BuildStampService).start()),
    provideAppInitializer(registerSensikaGridModules),
    // The help panel renders at document.body level through this service.
    provideAppInitializer(() => {
      inject(HelpPanelPortalService);
    }),
    StoreService,
    DatePipe,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    environment.production
      ? provideTanStackQuery(createQueryClient())
      : provideTanStackQuery(
          createQueryClient(),
          withDevtools(() => ({
            loadDevtools:
              localStorage.getItem('queryDevtools') === 'true' ? 'auto' : false,
          })),
        ),
  ],
};
